import React, { useEffect, useState, useRef } from "react";
import {
  MapPin,
  Plane,
  Building2,
  Search,
  Globe,
  User,
  ChevronDown,
  X,
  Clock,
  Calendar,
} from "lucide-react";
import { useFormContext } from "../../context/FormContext";
import { formatLocationWithType } from "../../utils/locationTypeUtils";
import {
  savePickupLocationTypeFilter,
  saveDropoffLocationTypeFilter,
  getPickupLocationTypeFilter,
} from "../../utils/locationTypeFilterUtils";
import CustomCalendar from "./CustomCalendar";
import CustomTimePicker from "./CustomTimePicker";
import countryList from "react-select-country-list";
import "react-datepicker/dist/react-datepicker.css";
import {
  dateToLocalString,
  stringToDate,
  formatDisplayDate,
  getDefaultAgeRange,
  getCountryName,
} from "./formUtils";
import useFormSubmit from "./useFormSubmit";

// ---------------------------------------------------------------------------
// AGE_OPTIONS — matches prototype range labels
// ---------------------------------------------------------------------------
const NORTH_AMERICA = ["US", "CA", "MX"];

const getDefaultAge = (countryCode) =>
  NORTH_AMERICA.includes(countryCode) ? "26-70" : "30-65";
const getDefaultCustomAge = (countryCode) =>
  NORTH_AMERICA.includes(countryCode) ? "26" : "30";

// ---------------------------------------------------------------------------
// AgeCheckbox
// Checked = use default age range for country
// Unchecked = user enters custom age (prefilled 35)
// ---------------------------------------------------------------------------
const AgeCheckbox = ({
  isDefault,
  customAge,
  countryCode,
  onToggle,
  onAgeChange,
}) => {
  const defaultRange = getDefaultAge(countryCode);
  const defaultCustom = getDefaultCustomAge(countryCode);
  return (
    <div className="flex items-center gap-2 h-7">
      {/* Checkbox */}
      <div
        className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all duration-200 shrink-0 cursor-pointer ${
          isDefault
            ? "bg-primary border-primary"
            : "bg-surface border-surface-border hover:border-primary"
        }`}
        onClick={onToggle}
      >
        {isDefault && (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-2.5 h-2.5 text-white"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        )}
      </div>

      {/* Label — always visible */}
      <span
        className="text-md font-semibold font-body text-dark whitespace-nowrap cursor-pointer"
        onClick={onToggle}
      >
        Age <span className="text-primary">{defaultRange}</span>
      </span>

      {/* Custom age input — only when unchecked, slides in */}
      <div
        className={`transition-all duration-200 overflow-hidden ${!isDefault ? "w-14 opacity-100" : "w-0 opacity-0"}`}
      >
        <input
          type="text"
          inputMode="numeric"
          min="21"
          max="99"
          value={customAge}
          onChange={(e) => onAgeChange(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          placeholder="35"
          className="w-14 h-7 bg-surface border border-surface-border rounded-lg text-center text-dark font-semibold text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
        />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// ResidencyDropdown
// ---------------------------------------------------------------------------
const ResidencyDropdown = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const ref = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);
  const countries = countryList().getData();

  const filtered = countries.filter((c) =>
    c.label.toLowerCase().includes(search.toLowerCase()),
  );

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (isOpen && searchRef.current) searchRef.current.focus();
  }, [isOpen]);

  useEffect(() => {
    setActiveIdx(0);
  }, [search]);

  useEffect(() => {
    if (listRef.current) {
      const item = listRef.current.childNodes[activeIdx];
      if (item) item.scrollIntoView({ block: "nearest" });
    }
  }, [activeIdx]);

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((p) => Math.min(p + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((p) => Math.max(p - 1, 0));
    } else if (e.key === "Enter" && filtered[activeIdx]) {
      onChange(filtered[activeIdx].value);
      setIsOpen(false);
      setSearch("");
    } else if (e.key === "Escape") {
      setIsOpen(false);
      setSearch("");
    }
  };

  return (
     <div ref={ref} className="relative w-full">
      <button
        type="button"
        onClick={() => setIsOpen((p) => !p)}
        className="flex items-center gap-2 w-full focus:outline-none"
      >
        <Globe className="w-4 h-4 text-primary shrink-0" />
        <span className="text-md font-semibold text-dark font-body flex-1 text-left truncate">
          {getCountryName(value) || "Select country"}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-full min-w-[280px] bg-surface border border-surface-border rounded-xl shadow-card z-[9999] overflow-hidden">
          <div className="p-2 border-b border-surface-border">
            <input
              ref={searchRef}
              type="text"
              placeholder="Search country..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              onClick={(e) => e.stopPropagation()}
              className="w-full px-3 py-2 text-sm font-body text-dark bg-surface-raised border border-surface-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40 placeholder:text-gray-400"
            />
          </div>
          <ul ref={listRef} className="max-h-52 overflow-y-auto">
            {filtered.length === 0 ? (
              <li className="px-4 py-3 text-sm text-gray-500 font-body">
                No results
              </li>
            ) : (
              filtered.map((c, i) => (
                <li
                  key={c.value}
                  onClick={() => {
                    onChange(c.value);
                    setIsOpen(false);
                    setSearch("");
                  }}
                  className={`px-4 py-2.5 cursor-pointer text-sm font-semibold font-body transition-colors ${
                    i === activeIdx
                      ? "bg-primary-soft text-primary"
                      : c.value === value
                        ? "bg-surface-raised text-dark"
                        : "text-dark hover:bg-surface-raised"
                  }`}
                >
                  {c.label}
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// FieldLabel
// ---------------------------------------------------------------------------
const FieldLabel = ({ children }) => (
  <p className="text-[10px] uppercase tracking-wide font-heading font-bold text-gray-500 mb-1">
    {children}
  </p>
);

// ---------------------------------------------------------------------------
// Divider — vertical separator between columns
// ---------------------------------------------------------------------------
const ColDivider = () => (
  <div className="hidden lg:block w-px bg-surface-border self-stretch" />
);

// ---------------------------------------------------------------------------
// Form
// ---------------------------------------------------------------------------
function Form({ variant = "default", onClose, onSearch }) {
  const {
    pickupGDSCode,
    setPickupGDSCode,
    dropOffGDSCode,
    setDropOffGDSCode,
    sameLocation,
    setSameLocation,
    pickup,
    setPickup,
    pickupLocationObj,
    setPickupLocationObj,
    dropoff,
    setDropoff,
    dropoffLocationObj,
    setDropoffLocationObj,
    pickupSuggestions,
    setPickupSuggestions,
    dropoffSuggestions,
    setDropoffSuggestions,
    pickupDate,
    pickupTime,
    dropoffDate,
    dropoffTime,
    driverCountry,
    setDriverCountry,
    driverAge,
    setDriverAge,
    fetchLocationSuggestions,
    userManuallySelectedCountry,
    setUserManuallySelectedCountry,
    canUseTestMode,
    isTestMode,
    setIsTestMode,
  } = useFormContext();

  // --- Draft state ---
  const initializedRef = useRef(false);
  // Restore GDS codes from localStorage if context is empty (e.g. after back-navigation)
  const ls = (key, fallback) => {
    try {
      const v = localStorage.getItem(key);
      return v ? JSON.parse(v) : fallback;
    } catch {
      return fallback;
    }
  };
  useEffect(() => {
    if (!pickupGDSCode) {
      const code = ls("form_pickupGDSCode", "");
      if (code) setPickupGDSCode(code);
    }
    if (!dropOffGDSCode) {
      const code = ls("form_dropOffGDSCode", "");
      if (code) setDropOffGDSCode(code);
    }
  }, []);


  const [draftPickup, setDraftPickup] = useState(
    pickup || ls("form_pickup", ""),
  );
  const [draftDropoff, setDraftDropoff] = useState(
    dropoff || ls("form_dropoff", ""),
  );
  const [draftSameLocation, setDraftSameLocation] = useState(
    sameLocation ?? ls("form_sameLocation", true),
  );
  const [draftPickupDate, setDraftPickupDate] = useState(
    pickupDate || ls("form_pickupDate", ""),
  );
  const [draftDropoffDate, setDraftDropoffDate] = useState(
    dropoffDate || ls("form_dropoffDate", ""),
  );
  const [draftPickupTime, setDraftPickupTime] = useState(
    pickupTime || ls("form_pickupTime", "10:00"),
  );
  const [draftDropoffTime, setDraftDropoffTime] = useState(
    dropoffTime || ls("form_dropoffTime", "10:00"),
  );
  const [draftDriverCountry, setDraftDriverCountry] = useState(
    driverCountry || ls("form_driverCountry", "US"),
  );
  const isDefaultAgeValue = (age) =>
    age === "26-70" || age === "30-65";

  const [isAgeDefault, setIsAgeDefault] = useState(() => {
    const saved = driverAge || ls("form_driverAge", "35");
    return isDefaultAgeValue(saved);
  });

  const [customAge, setCustomAge] = useState(() => {
    const saved = driverAge || ls("form_driverAge", "35");
    return isDefaultAgeValue(saved)
      ? getDefaultCustomAge(draftDriverCountry)
      : saved;
  });

  const [draftDriverAge, setDraftDriverAge] = useState(() => {
    const saved = driverAge || ls("form_driverAge", "35");
    return saved || getDefaultAge(driverCountry || "US");
  });

  // --- UI state ---
  const [focusedPickupIndex, setFocusedPickupIndex] = useState(0);
  const [focusedDropoffIndex, setFocusedDropoffIndex] = useState(0);
  const [isPickupCalendarOpen, setIsPickupCalendarOpen] = useState(false);
  const [isDropoffCalendarOpen, setIsDropoffCalendarOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // ---------------------------------------------------------------------------
  // Initialization
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (pickup && !draftPickup) setDraftPickup(pickup);
    if (dropoff && !draftDropoff) setDraftDropoff(dropoff);
    if (pickupDate && !draftPickupDate) setDraftPickupDate(pickupDate);
    if (dropoffDate && !draftDropoffDate) setDraftDropoffDate(dropoffDate);
  }, [pickup, dropoff, pickupDate, dropoffDate]);

  useEffect(() => {
    if (!draftPickup && pickup) setDraftPickup(pickup);
  }, [pickup]);
  useEffect(() => {
    if (!draftDropoff && dropoff) setDraftDropoff(dropoff);
  }, [dropoff]);

  // ---------------------------------------------------------------------------
  // Residency auto-detect sync
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (driverCountry && !userManuallySelectedCountry) {
      setDraftDriverCountry(driverCountry);
      setDraftDriverAge(getDefaultAge(driverCountry));
      setIsAgeDefault(true);
    }
  }, [driverCountry, userManuallySelectedCountry]);

  useEffect(() => {
    const sync = (e) => {
      if (e.key === "form_driverCountry" && e.newValue)
        setDriverCountry(JSON.parse(e.newValue));
      if (e.key === "form_manualCountry" && e.newValue)
        setUserManuallySelectedCountry(JSON.parse(e.newValue));
    };
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, [setDriverCountry, setUserManuallySelectedCountry]);

  // ---------------------------------------------------------------------------
  // Suggestion scroll
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const t = setTimeout(() => {
      const el = document.querySelector(".suggestion-item-active");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 50);
    return () => clearTimeout(t);
  }, [focusedPickupIndex, focusedDropoffIndex]);

  // ---------------------------------------------------------------------------
  // Pickup suggestions
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!draftPickup || draftPickup.trim().length < 1) {
      setPickupSuggestions([]);
      return;
    }

    if (pickupGDSCode && draftPickup === pickup) return;
    const delay = draftPickup.length > 15 ? 100 : 350;
    const t = setTimeout(() => {
      fetchLocationSuggestions(draftPickup).then((results) => {
        const s = results || [];
        setPickupSuggestions(s);
        if (draftPickup.length > 15 && s.length > 0) {
          const best = s[0];
          const id = best.location_id || best.gdsCode || "";
          // Only fill in what is missing — don't overwrite a code that was
          // already restored by the URL→context sync in SearchResults.
          if (!pickupLocationObj) setPickupLocationObj(best);
          if (!pickupGDSCode) setPickupGDSCode(id);
          if (draftSameLocation) {
            setDraftDropoff(draftPickup);
            if (!dropoffLocationObj) setDropoffLocationObj(best);
            if (!dropOffGDSCode) setDropOffGDSCode(id);
          }
        }
      });
    }, delay);
    return () => clearTimeout(t);
  }, [
    draftPickup,
    fetchLocationSuggestions,
    setPickupSuggestions,
    draftSameLocation,
  ]);

  // ---------------------------------------------------------------------------
  // Dropoff suggestions
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (draftSameLocation || !draftDropoff || draftDropoff.trim().length < 1) {
      setDropoffSuggestions([]);
      return;
    }
    const t = setTimeout(() => {
      fetchLocationSuggestions(draftDropoff).then((r) =>
        setDropoffSuggestions(r || []),
      );
    }, 350);
    return () => clearTimeout(t);
  }, [
    draftDropoff,
    draftSameLocation,
    fetchLocationSuggestions,
    setDropoffSuggestions,
  ]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const handleSameLocationToggle = () => {
    const next = !draftSameLocation;
    setDraftSameLocation(next);
    setSameLocation(next);
    if (next) {
      setDraftDropoff(draftPickup);
      setDropoff(draftPickup);
      if (pickupLocationObj) setDropoffLocationObj(pickupLocationObj);
      if (pickupGDSCode) setDropOffGDSCode(pickupGDSCode);
      setDropoffSuggestions([]);
      const t = getPickupLocationTypeFilter();
      if (t) saveDropoffLocationTypeFilter(t);
    } else {
      setDraftDropoff("");
      setDropoff("");
      setDropoffLocationObj(null);
      setDropOffGDSCode("");
      setDropoffSuggestions([]);
      saveDropoffLocationTypeFilter(null);
    }
  };

  const handlePickupDateSelect = (date) => {
    if (!date) return;
    setDraftPickupDate(dateToLocalString(date));
    setIsPickupCalendarOpen(false);
    setTimeout(() => setIsDropoffCalendarOpen(true), 150);
  };

  const handleDropoffDateSelect = (date) => {
    if (!date) return;
    setDraftDropoffDate(dateToLocalString(date));
    setIsDropoffCalendarOpen(false);
  };

  const handlePickupSelect = (loc) => {
    const id = loc.location_id || loc.gdsCode || "";
    const { displayText } = formatLocationWithType(loc);
    setPickupLocationObj(loc);
    setDraftPickup(displayText);
    setPickup(displayText);
    setPickupGDSCode(id);
    setPickupSuggestions([]);
    savePickupLocationTypeFilter(
      loc.is_airport ? "In-Terminal" : "Off-Terminal",
    );
    if (draftSameLocation) {
      setDraftDropoff(displayText);
      setDropoff(displayText);
      setDropoffLocationObj(loc);
      setDropOffGDSCode(id);
      saveDropoffLocationTypeFilter(
        loc.is_airport ? "In-Terminal" : "Off-Terminal",
      );
    }
  };

  const handleDropoffSelect = (loc) => {
    const id = loc.location_id || loc.gdsCode || "";
    const { displayText } = formatLocationWithType(loc);
    setDropoffLocationObj(loc);
    setDraftDropoff(displayText);
    setDropOffGDSCode(id);
    setDropoffSuggestions([]);
  };

  const handleClearPickup = () => {
    setDraftPickup("");
    setPickup("");
    setPickupLocationObj(null);
    setPickupGDSCode("");
    setPickupSuggestions([]);
    if (draftSameLocation) {
      setDraftDropoff("");
      setDropoff("");
      setDropoffLocationObj(null);
      setDropOffGDSCode("");
    }
  };

  const handleClearDropoff = () => {
    setDraftDropoff("");
    setDropoff("");
    setDropoffLocationObj(null);
    setDropOffGDSCode("");
    setDropoffSuggestions([]);
  };

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------
  const { handleSubmit } = useFormSubmit({
    draftPickup,
    draftDropoff,
    draftPickupDate,
    draftDropoffDate,
    draftPickupTime,
    draftDropoffTime,
    draftSameLocation,
    draftDriverCountry,
    isAgeDefault,
    customAge,
    isTestMode,
    onSearch,
    onClose,
    setValidationErrors,
  });

  // ---------------------------------------------------------------------------
  // Suggestion list — shared renderer
  // ---------------------------------------------------------------------------
  const SuggestionList = ({ suggestions, focusedIndex, onSelect }) => (
    <ul className="absolute left-0 right-0 top-full bg-surface border border-surface-border rounded-xl shadow-card mt-1 max-h-[280px] overflow-y-auto z-[9999]">
      {suggestions.map((loc, i) => {
        const { mainText, subText } = formatLocationWithType(loc);
        const Icon = loc.is_airport ? Plane : Building2;
        return (
          <li
            key={i}
            onClick={() => onSelect(loc)}
            className={`px-4 py-3 cursor-pointer flex items-center gap-3 transition-colors ${
              focusedIndex === i
                ? "bg-primary-soft suggestion-item-active"
                : "hover:bg-surface-raised"
            }`}
          >
            <Icon size={16} className="text-primary shrink-0" />
            <div className="flex flex-col flex-1 min-w-0">
              <span className="text-sm font-semibold text-dark font-body truncate">
                {mainText}
              </span>
              <span className="text-[11px] text-gray-500 font-body truncate">
                {subText}
              </span>
            </div>
            {loc.is_airport && (
              <span className="text-[9px] px-2 py-0.5 rounded-full bg-primary-soft text-primary font-bold uppercase shrink-0">
                Airport
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="w-full">
      <div className="bg-surface rounded-2xl border border-surface-border shadow-card w-full overflow-visible">
        <form onSubmit={handleSubmit}>
          {/* ── ROW 1: Location(s) ── */}
          <div
            className={`flex flex-col lg:flex-row ${!draftSameLocation ? "lg:divide-x divide-surface-border" : ""} px-5 pt-3 pb-2`}
          >
            {/* Pickup location */}
            <div
              className={`relative ${!draftSameLocation ? "lg:flex-1 lg:pr-5 pb-3 lg:pb-0" : "w-full"}`}
            >
              <div className="flex items-center justify-between mb-1">
                <FieldLabel>Pick-up location</FieldLabel>
                {draftSameLocation && (
                  <button
                    type="button"
                    onClick={handleSameLocationToggle}
                    className="text-[16px] text-primary font-semibold font-body hover:underline"
                  >
                    + Different drop-off
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-primary shrink-0" />
                <input
                  type="text"
                  placeholder="Enter city or airport"
                  value={draftPickup}
                  onChange={(e) => {
                    setDraftPickup(e.target.value);
                    setPickupLocationObj(null);
                    setPickupGDSCode("");
                    setPickup("");
                    setFocusedPickupIndex(0);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowDown") {
                      e.preventDefault();
                      setFocusedPickupIndex((p) =>
                        Math.min(p + 1, pickupSuggestions.length - 1),
                      );
                    } else if (e.key === "ArrowUp") {
                      e.preventDefault();
                      setFocusedPickupIndex((p) => Math.max(p - 1, 0));
                    } else if (e.key === "Enter") {
                      const t =
                        pickupSuggestions[focusedPickupIndex] ||
                        pickupSuggestions[0];
                      if (t) {
                        e.preventDefault();
                        handlePickupSelect(t);
                        setFocusedPickupIndex(0);
                      }
                    }
                  }}
                  className="flex-1 bg-transparent border-none outline-none text-md font-semibold font-body text-dark placeholder:text-gray-400 placeholder:font-normal"
                />
                {draftPickup && (
                  <button
                    type="button"
                    onClick={handleClearPickup}
                    className="text-gray-500 hover:text-dark transition-colors"
                  >
                    <X size={15} />
                  </button>
                )}
              </div>
              {pickupSuggestions?.length > 0 && (
                <SuggestionList
                  suggestions={pickupSuggestions}
                  focusedIndex={focusedPickupIndex}
                  onSelect={handlePickupSelect}
                />
              )}
            </div>

            {/* Dropoff location — only when different */}
            {!draftSameLocation && (
              <div className="relative lg:flex-1 lg:pl-5 pt-3 lg:pt-0 border-t lg:border-t-0 border-surface-border">
                <div className="flex items-center justify-between mb-1">
                  <FieldLabel>Drop-off location</FieldLabel>
                  <button
                    type="button"
                    onClick={handleSameLocationToggle}
                    className="text-[16px] text-gray-500 font-semibold font-body hover:text-dark"
                  >
                    + Remove drop-off
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-primary shrink-0" />
                  <input
                    type="text"
                    placeholder="Different drop-off city or airport"
                    value={draftDropoff}
                    onChange={(e) => {
                      setDraftDropoff(e.target.value);
                      setDropoffLocationObj(null);
                      setDropOffGDSCode("");
                      setFocusedDropoffIndex(0);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "ArrowDown") {
                        e.preventDefault();
                        setFocusedDropoffIndex((p) =>
                          Math.min(p + 1, dropoffSuggestions.length - 1),
                        );
                      } else if (e.key === "ArrowUp") {
                        e.preventDefault();
                        setFocusedDropoffIndex((p) => Math.max(p - 1, 0));
                      } else if (e.key === "Enter") {
                        const t =
                          dropoffSuggestions[focusedDropoffIndex] ||
                          dropoffSuggestions[0];
                        if (t) {
                          e.preventDefault();
                          handleDropoffSelect(t);
                          setFocusedDropoffIndex(0);
                        }
                      }
                    }}
                    className="flex-1 bg-transparent border-none outline-none text-md font-semibold font-body text-dark placeholder:text-gray-400 placeholder:font-normal"
                  />
                  {draftDropoff && (
                    <button
                      type="button"
                      onClick={handleClearDropoff}
                      className="text-gray-500 hover:text-dark transition-colors"
                    >
                      <X size={15} />
                    </button>
                  )}
                </div>
                {dropoffSuggestions?.length > 0 && (
                  <SuggestionList
                    suggestions={dropoffSuggestions}
                    focusedIndex={focusedDropoffIndex}
                    onSelect={handleDropoffSelect}
                  />
                )}
              </div>
            )}
          </div>

          <div className="h-px bg-surface-border" />

          {/* ── ROW 2: Pickup Date | Pickup Time | Dropoff Date | Dropoff Time ── */}
          <div className="grid grid-cols-2 lg:grid-cols-4 divide-surface-border px-5 py-2 gap-0 lg:divide-x lg:flex lg:flex-row">
            {/* Pickup date */}
            <div className="relative flex-1 pr-3 lg:pr-5 pb-3 lg:pb-0 border-r border-surface-border">
              <FieldLabel>Pick-up Date</FieldLabel>
              <div
                data-calendar-trigger
                className="flex items-center gap-2 cursor-pointer"
                onClick={() => {
                  setIsPickupCalendarOpen(true);
                  setIsDropoffCalendarOpen(false);
                }}
              >
                <Calendar className="text-primary w-4 h-4 shrink-0" />
                <span
                  className={`text-md font-semibold font-body ${draftPickupDate ? "text-dark" : "text-gray-500"}`}
                >
                  {draftPickupDate
                    ? formatDisplayDate(draftPickupDate)
                    : "Select date"}
                </span>
              </div>
              {isPickupCalendarOpen && (
                <div className="absolute top-full left-0 mt-2 z-[9999]">
                  <CustomCalendar
                    selectedStartDate={stringToDate(draftPickupDate)}
                    selectedEndDate={stringToDate(draftDropoffDate)}
                    onDateSelect={handlePickupDateSelect}
                    onClose={() => setIsPickupCalendarOpen(false)}
                    isRangeSelection={false}
                    showRange={true}
                    minDate={new Date()}
                    isMobile={window.innerWidth < 1024}
                  />
                </div>
              )}
            </div>

            {/* Pickup time */}
            <div className="flex-1 pl-3 lg:px-5 pb-3 lg:pb-0">
              <FieldLabel>Pickup Time</FieldLabel>
              <div className="flex items-center gap-2">
                <CustomTimePicker
                  selectedTime={draftPickupTime}
                  onTimeSelect={(t) => setDraftPickupTime(t)}
                  isMobile={false}
                  driverCountry={draftDriverCountry}
                />
                <Clock className="w-4 h-4 text-primary shrink-0 ml-auto" />
              </div>
            </div>

            {/* Dropoff date */}
            <div className="relative flex-1 pr-3 lg:px-5 pt-3 lg:pt-0 border-r border-surface-border lg:border-t-0 border-t">
              <FieldLabel>Drop-off Date</FieldLabel>
              <div
                data-calendar-trigger
                className="flex items-center gap-2 cursor-pointer"
                onClick={() => {
                  setIsDropoffCalendarOpen(true);
                  setIsPickupCalendarOpen(false);
                }}
              >
                <Calendar className="text-primary w-4 h-4 shrink-0" />
                <span
                  className={`text-md font-semibold font-body ${draftDropoffDate ? "text-dark" : "text-gray-500"}`}
                >
                  {draftDropoffDate
                    ? formatDisplayDate(draftDropoffDate)
                    : "Select date"}
                </span>
              </div>
              {isDropoffCalendarOpen && (
                <div className="absolute top-full left-0 mt-2 z-[9998]">
                  <CustomCalendar
                    selectedStartDate={stringToDate(draftPickupDate)}
                    selectedEndDate={stringToDate(draftDropoffDate)}
                    onDateSelect={handleDropoffDateSelect}
                    onClose={() => setIsDropoffCalendarOpen(false)}
                    isRangeSelection={false}
                    singleMonth={window.innerWidth < 1024}
                    minDate={stringToDate(draftPickupDate) || new Date()}
                    pickupDate={stringToDate(draftPickupDate)}
                    isMobile={window.innerWidth < 1024}
                  />
                </div>
              )}
            </div>

            {/* Dropoff time */}
            <div className="flex-1 pl-3 lg:pl-5 pt-3 lg:pt-0 lg:border-t-0 border-t border-surface-border">
              <FieldLabel>Drop-off Time</FieldLabel>
              <div className="flex items-center gap-2">
                <CustomTimePicker
                  selectedTime={draftDropoffTime}
                  onTimeSelect={(t) => setDraftDropoffTime(t)}
                  isMobile={false}
                  driverCountry={draftDriverCountry}
                />
                <Clock className="w-4 h-4 text-primary shrink-0 ml-auto" />
              </div>
            </div>
          </div>

          <div className="h-px bg-surface-border" />

          {/* ── ROW 3: Driver Age | Residency | Search button ── */}
           {/* ── ROW 3: Driver Age | Residency | Search button ── */}
<div className="flex flex-col lg:flex-row lg:divide-x divide-surface-border px-5 py-2 lg:items-center gap-3 lg:gap-0">
  {/* Driver Age */}
  <div className="w-full lg:flex-1 lg:pr-5 pb-2 lg:pb-0">
    <FieldLabel>Driver Age</FieldLabel>
    <AgeCheckbox
      isDefault={isAgeDefault}
      customAge={customAge}
      countryCode={draftDriverCountry}
      onToggle={() => {
        const next = !isAgeDefault;
        setIsAgeDefault(next);
        if (next) {
          const def = getDefaultAge(draftDriverCountry);
          setDraftDriverAge(def);
          setDriverAge(def);
        } else {
          const def = getDefaultCustomAge(draftDriverCountry);
          setCustomAge(def);
          setDraftDriverAge(def);
          setDriverAge(def);
        }
      }}
      onAgeChange={(val) => {
        setCustomAge(val);
        setDraftDriverAge(val);
        setDriverAge(val);
        const num = parseInt(val, 10);
        if (val && (isNaN(num) || num < 21 || num > 99)) {
          setValidationErrors((prev) => ({ ...prev, driverAge: "Age must be between 21 and 99" }));
        } else {
          setValidationErrors((prev) => ({ ...prev, driverAge: "" }));
        }
      }}
    />
  </div>

  {/* Residency — now dynamically expands to fit full country names */}
  <div className="w-full lg:flex-1 lg:px-5 pt-2 lg:pt-0 border-t lg:border-t-0 border-surface-border">
    <FieldLabel>Residency</FieldLabel>
    <ResidencyDropdown
      value={draftDriverCountry}
      onChange={(val) => {
        setDraftDriverCountry(val);
        setDriverCountry(val);
        setUserManuallySelectedCountry(true);
        setDraftDriverAge(getDefaultAge(val));
        setDriverAge(getDefaultAge(val));
        setIsAgeDefault(true);
      }}
    />
  </div>

  {/* Search button */}
  <div className="w-full lg:flex-[2] lg:pl-5 pt-2 lg:pt-0">
    <button
      type="submit"
      className="w-full h-11 bg-primary hover:bg-primary-hover text-white rounded-xl font-heading font-bold text-sm tracking-wide transition-all shadow-primary-glow flex items-center justify-center gap-2 whitespace-nowrap"
    >
      <Search className="w-4 h-4" />
      Search Cars
    </button>
  </div>
</div>
        </form>
      </div>
    </div>
  );
}

export default Form;